import classNames from "classnames";
import moment from "moment";
import { useContext, useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";

import MobileHeader from "src/app/components/datatable/header/MobileHeader";
import TableHeader from "src/app/components/datatable/header/TableHeader";
import MobileActions from "src/app/components/datatable/miscellaneous/MobileActions";
import Pagination from "src/app/components/datatable/pagination/Pagination";
import { Icon } from "src/app/components/icon/Icon";
import Loader from "src/app/components/loader/Loader";
import DeleteModal from "src/app/components/modal/DeleteModal";
import { ModalContent } from "src/app/components/modal/types";
import projectCSS from "src/app/modules/user-projects/Projects.module.css";
import iconOptions from "src/assets/iconsGraphics/optionDots.svg";
import { ROLE, ROUTES } from "src/config/constants";
import { ModalContext } from "src/contexts/ModalContext";
import { ApiResult } from "src/core/helpers/api/ApiResult";
import { sortFieldGenerator } from "src/core/helpers/global/global";
import { AccountResponse } from "src/core/models/auth";
import { Column } from "src/core/models/global/Column";
import { ProjectResponse } from "src/core/models/projects/ProjectResponse";
import { AccountsService } from "src/core/services/auth";
import { ProjectsService } from "src/core/services/projects/ProjectsService";
import { TableHead } from "src/types/global";

import PartnerModal from "./components/PartnerModal";

const headers: Array<TableHead> = [
  {
    name: "Šifra upita",
    field: "id",
    sortable: true,
    class: "u-w10 u-cursor-pointer u-t-align-start u-t-uppercase u-t-secondary u-t-f16p",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
  {
    name: "Naziv Projekta",
    field: "name",
    sortable: true,
    class: "u-w15 u-cursor-pointer u-t-align-start u-t-uppercase u-t-secondary u-t-f16p",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
  {
    name: "Poslano Partneru",
    field: "partner.name",
    sortable: true,
    class: "u-w20 u-cursor-pointer u-t-align-start u-t-uppercase u-t-secondary u-t-f16p",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
  {
    name: "Kreirano",
    field: "sysTimeCreated",
    sortable: true,
    class: "u-w10 u-cursor-pointer u-t-align-start u-t-uppercase u-t-secondary u-t-f16p",
    mclass: "u-mb20px u-t-f16p u-t-lh15",
  },
  { name: "", field: "", sortable: false, class: "u-w45 u-t-align-end u-t-uppercase u-t-secondary u-t-f16p", mclass: "u-mb20px u-t-f16p u-t-lh15" },
];

const Projects = () => {
  const [user, setUser] = useState<AccountResponse>();
  const [projects, setProjects] = useState<Array<ProjectResponse>>([]);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(6);
  const [numberOfPages, setNumberOfPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sorting, setSorting] = useState<Column>({ name: "sysTimeCreated", sortDescending: true });
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteModalContent, setDeleteModalContent] = useState<ModalContent>();
  const [headerMenuActive, setHeaderMenuActive] = useState(false);
  const headerMenuRef = useRef<Array<any>>([]);
  const setHeaderMenuRef = (key: number, ref: any) => {
    headerMenuRef.current[key] = ref;
  };
  const [actionsMenuActive, setActionsMenuActive] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectResponse>();
  const [renamingProjectId, setRenamingProjectId] = useState<number>(0);

  const [partnerModalOpened, setPartnerModalOpened] = useState(false);
  const [partnerModalSelectedProject, setPartnerModalSelectedProject] = useState<ProjectResponse>();
  const [searchInputValue, setSearchInputValue] = useState("");

  const actionsMenuRef = useRef<Array<any>>([]);
  const setActionsMenuRef = (key: number, ref: any) => {
    actionsMenuRef.current[key] = ref;
  };
  const modalData = useContext(ModalContext);

  const { userId }: any = useParams();
  let history = useHistory();
  const location = useLocation();
  const isAdminLocation = location.pathname.includes("admin");

  useEffect(() => {
    if (loadingProjects && user && user.id) {
      const timeoutID = setTimeout(() => {
        setHeaderMenuActive(false);
        const sort = sortFieldGenerator(sorting.name ?? "", sorting.sortDescending ?? false);
        ProjectsService.getByAccountId(user!.id!, searchInputValue, recordsPerPage, currentPage, sort)
          .then((response) => {
            if (response?.records) {
              setProjects(response.records);
              setRecordsPerPage(response.pageSize ?? 0);
              setNumberOfPages(response.totalNumOfPages ?? 0);
            }
          })
          .catch((e: ApiResult) => {
            if (!modalData?.content?.contentSet) {
              modalData?.triggerError(e);
            }
          })
          .finally(() => {
            setLoadingProjects(false);
          });
      }, 500);
      return () => clearTimeout(timeoutID);
    }
  }, [loadingProjects, searchInputValue, currentPage, sorting, user, recordsPerPage, modalData]);

  useEffect(() => {
    if (loadingUser) {
      AccountsService.getById(userId)
        .then((response) => {
          if (response) {
            setUser(response);
            setLoadingProjects(true);
          }
        })
        .catch((e: ApiResult) => {
          if (!modalData?.content?.contentSet) {
            modalData?.triggerError(e);
          }
        })
        .finally(() => {
          setLoadingUser(false);
        });
    }
  }, [loadingUser, userId, modalData]);

  useEffect(() => {
    setLoadingProjects(true);
  }, [currentPage, sorting]);

  function handleDeleteModal(project: ProjectResponse | undefined) {
    if (project) {
      setDeleteModalContent({
        entityId: project.id,
        header: "Izbriši projekt",
        text: "Jeste li sigurni da želite izbrisati projekt",
        highlightedText: project.name,
      });
      setDeleteModalOpen(true);
    }
  }

  function deleteProject(projectId: number) {
    if (projectId) {
      setLoadingProjects(true);
      ProjectsService.delete(projectId)
        .then((response: any) => {
          if (response) {
            const newProjects = projects.filter((pr) => pr.id !== projectId);
            setProjects(newProjects);
            modalData?.triggerSuccess(response);
          }
        })
        .catch((e: ApiResult) => {
          modalData?.triggerError(e);
        })
        .finally(() => {
          setLoadingProjects(false);
          setDeleteModalOpen(false);
        });
    }
  }

  function renameProject(projectId: number, projectName: string) {
    if (projectId) {
      setLoadingProjects(true);
      ProjectsService.rename({ id: projectId, name: projectName })
        .then((response: any) => {
          if (response) {
            const renamedProject = projects.find((pr) => pr.id === projectId);
            if (renamedProject) {
              renamedProject.name = projectName ?? "";
            }
            setRenamingProjectId(0);
            let copyOfProjects = [...projects.map((x) => x)];
            setProjects(copyOfProjects);
            modalData?.triggerSuccess(response);
          }
        })
        .catch((e: ApiResult) => {
          modalData?.triggerError(e);
        })
        .finally(() => {
          setLoadingProjects(false);
        });
    }
  }

  function openProjectRename(projectId: number | undefined) {
    if (projectId) {
      setActionsMenuActive(false);
      setRenamingProjectId(projectId);
    }
  }

  const openPartnerModal = (project: ProjectResponse | undefined) => {
    if (project) {
      setPartnerModalOpened(true);
      setPartnerModalSelectedProject(project);
      setActionsMenuActive(false);
    }
  };

  function sendToMail(projectId: number | undefined) {
    if (projectId) {
      setActionsMenuActive(false);
      setLoadingAction(true);
      ProjectsService.sendToUser(projectId)
        .then((response: any) => {
          modalData?.triggerSuccess(response);
        })
        .catch((e: ApiResult) => {
          modalData?.triggerError(e);
        })
        .finally(() => {
          setLoadingAction(false);
        });
    }
  }

  function downloadProject(projectId: number | undefined, projectName: string) {
    if (projectId) {
      setActionsMenuActive(false);
      setLoadingAction(true);
      ProjectsService.getPdf(projectId)
        .then((response: any) => {
          const url = window.URL.createObjectURL(response);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", projectName + ".pdf");
          document.body.appendChild(link);
          link.click();
        })
        .catch((e: ApiResult) => {
          modalData?.triggerError(e);
        })
        .finally(() => {
          setLoadingAction(false);
        });
    }
  }

  function downloadSystemProjectPdf(projectId: number | undefined, projectName: string) {
    if (projectId) {
      setActionsMenuActive(false);
      setLoadingAction(true);
      ProjectsService.getSystemProjectPdf(projectId)
        .then((response: any) => {
          const url = window.URL.createObjectURL(response);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", projectName + ".pdf");
          document.body.appendChild(link);
          link.click();
        })
        .catch((e: ApiResult) => {
          modalData?.triggerError(e);
        })
        .finally(() => {
          setLoadingAction(false);
        });
    }
  }

  function downloadSystemProjectExcel(projectId: number | undefined, projectName: string) {
    if (projectId) {
      setActionsMenuActive(false);
      setLoadingAction(true);

      ProjectsService.getSystemProjectExcel(projectId)
        .then((response: any) => {
          const url = window.URL.createObjectURL(response);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", projectName + ".xlsx");
          document.body.appendChild(link);
          link.click();
        })
        .catch((e: ApiResult) => {
          modalData?.triggerError(e);
        })
        .finally(() => {
          setLoadingAction(false);
        });
    }
  }

  const handleSearchInputChange: any = (event: any) => {
    setSearchInputValue(event.target.value);
    setLoadingProjects(true);
  };

  const resetSearchInputField = () => {
    setSearchInputValue("");
    setLoadingProjects(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!headerMenuRef.current[0]?.contains(event.target as Node) && headerMenuActive) {
        setHeaderMenuActive(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
  }, [headerMenuRef, headerMenuActive]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!actionsMenuRef.current[0]?.contains(event.target as Node) && actionsMenuActive) {
        setActionsMenuActive(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
  }, [actionsMenuRef, actionsMenuActive]);

  return (
    <div className="u-bg-background u-pos-relative">
      {/* Desktop users HTML */}
      <div className={classNames("u-box", "u-flex-wrap", "u-t-align-center", projectCSS.tableContainer)}>
        <div>{user && <Header user={user} isAdminLocation={isAdminLocation} />}</div>
        {isAdminLocation && (
          <div className={classNames("u-flex", "u-flex-content-start", projectCSS.backBtnContainer)}>
            <button
              className="u-t-primary-dark u-cursor-pointer u-t-uppercase u-t-f16p u-t-lh15 u-font-family-inh u-t-nodecoration u-t-w400 u-cursor-pointer"
              onClick={history.goBack}
            >
              Nazad
            </button>
          </div>
        )}
        <div className="u-pos-relative">
          <div className={classNames("u-flex", "u-flex-content-between", "u-flex-items-center", projectCSS.searchGroupNew)}>
            <button
              type="button"
              className={classNames("u-z-index1", "u-t-black", "u-t-f15p", "u-p0", "u-pos-absolute", "u-cursor-pointer", projectCSS.searchButtonNew)}
            >
              <Icon name="search" className="u-t-valign-mid" />
            </button>
            <input value={searchInputValue} onChange={handleSearchInputChange} type="text" placeholder="Pretraži" />
            <button
              type="button"
              onClick={resetSearchInputField}
              className={classNames("u-z-index1", "u-t-secondary", "u-t-f10p", "u-p0", "u-none", "u-cursor-pointer", projectCSS.clearButtonNew)}
            >
              <Icon name="times" className="u-t-valign-mid" />
            </button>
          </div>
        </div>
        <div className={classNames(projectCSS.tableWrap)}>
          <table className="u-w100 u-table-fixed">
            <TableHeader defaultSort={sorting} headers={headers} onSorting={setSorting} />
            <tbody>
              {!projects.length && !loadingProjects && (
                <tr className="u-col-12 u-t-halign-c">
                  <td colSpan={5}>Nema upita</td>
                </tr>
              )}
              {!loadingProjects &&
                projects.map((project: ProjectResponse) => (
                  <Row
                    user={user}
                    key={project.id}
                    project={project}
                    isAdminLocation={isAdminLocation}
                    handleDelete={(project: ProjectResponse) => handleDeleteModal(project)}
                    onProjectRename={(projectId: number, projectName: string) => renameProject(projectId, projectName)}
                    openPartnerModal={(project: ProjectResponse | undefined) => openPartnerModal(project)}
                    sendToMail={(projectId: number | undefined) => sendToMail(projectId)}
                    downloadProject={(projectId: number | undefined, projectName: string) => downloadProject(projectId, projectName)}
                    downloadSystemProjectPdf={(projectId: number | undefined, projectName: string) => downloadSystemProjectPdf(projectId, projectName)}
                    downloadSystemProjectExcel={(projectId: number | undefined, projectName: string) => downloadSystemProjectExcel(projectId, projectName)}
                  />
                ))}
            </tbody>
          </table>
          {loadingAction && <Loader />}
        </div>
      </div>

      {/* Mobile projects HTML */}
      <div className={projectCSS.mobileTabs}>
        {headerMenuActive && (
          <MobileHeader
            setRef={setHeaderMenuRef}
            defaultSort={sorting}
            headers={headers.filter((head) => head.sortable)}
            onSorting={(column: Column) => {
              setSorting(column);
              setHeaderMenuActive(false);
            }}
          />
        )}
        {actionsMenuActive && (
          <MobileActions
            user={user}
            project={selectedProject}
            isAdminLocation={isAdminLocation}
            handleDelete={(project: ProjectResponse | undefined) => handleDeleteModal(project)}
            openProjectRename={(projectId: number | undefined) => openProjectRename(projectId)}
            openPartnerModal={(project: ProjectResponse | undefined) => openPartnerModal(project)}
            downloadProject={(projectId: number | undefined, projectName: string) => downloadProject(projectId, projectName)}
            downloadSystemProjectPdf={(projectId: number | undefined, projectName: string) => downloadSystemProjectPdf(projectId, projectName)}
            downloadSystemProjectExcel={(projectId: number | undefined, projectName: string) => downloadSystemProjectExcel(projectId, projectName)}
            sendToMail={(projectId: number | undefined) => sendToMail(projectId)}
            setRef={setActionsMenuRef}
          />
        )}
        <div className={classNames("u-bg-background", projectCSS.containerMobile)}>
          {isAdminLocation && (
            <div className={classNames("u-flex", "u-flex-content-start", projectCSS.backBtnContainer)}>
              <button
                className="u-t-primary-dark u-cursor-pointer u-t-uppercase u-t-f16p u-t-lh15 u-font-family-inh u-t-nodecoration u-t-w400 u-cursor-pointer"
                onClick={history.goBack}
              >
                Nazad
              </button>
            </div>
          )}
          <div className={classNames("u-t-halign-c u-mb20px", isAdminLocation ? "u-mt10px" : "u-mt60px")}>
            {user && <Header user={user} isAdminLocation={isAdminLocation} />}
          </div>
          {projects.length && !loadingProjects && (
            <div
              onClick={() => setHeaderMenuActive(true)}
              className={classNames("u-flex u-flex-items-center u-mb20px u-col-xs-12 u-col-sm-12 u-cursor-pointer", projectCSS.mobileFilterCont)}
            >
              <label className="u-t-secondary u-mr10px u-t-f18p u-t-lh15">Poredaj po:</label>
              <span className="u-mr10px u-t-f18p u-t-lh15 u-t-lowercase">{headers.find((x) => x.field === sorting.name)?.name}</span>
              <Icon name={sorting.sortDescending ? "arrow-down" : "arrow-up"} className="u-t-f18p u-t-primary" />
            </div>
          )}
          {loadingAction && <Loader />}
          <ul className="u-m0">
            {!projects.length && !loadingProjects && (
              <li>
                <h2 className="u-col-12 u-t-halign-c u-t-secondary u-t-uppercase">Nema upita</h2>
              </li>
            )}
            {/* List item */}
            {!loadingProjects &&
              projects.map((project: ProjectResponse) => (
                <ListItem
                  key={project.id}
                  project={project}
                  isAdminLocation={isAdminLocation}
                  isRenameActive={project.id === renamingProjectId}
                  onProjectRename={(projectId: number, projectName: string) => renameProject(projectId, projectName)}
                  onProjectRenameCancel={() => setRenamingProjectId(0)}
                  openActions={() => {
                    setSelectedProject(project);
                    setActionsMenuActive(true);
                  }}
                />
              ))}
          </ul>
        </div>
      </div>

      {loadingProjects && <Loader />}
      <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} numberOfPages={numberOfPages} onPageChange={setCurrentPage} />
      {deleteModalOpen && deleteModalContent && (
        <DeleteModal close={(isOpen: boolean) => setDeleteModalOpen(isOpen)} deleteEntity={deleteProject} modalContent={deleteModalContent} />
      )}
      {partnerModalOpened && (
        <PartnerModal
          selectedProject={partnerModalSelectedProject}
          hideModal={() => setPartnerModalOpened(false)}
          handleProjectSent={() => {}}
          handlePartnerSelected={() => {}}
        />
      )}
    </div>
  );
};

export default Projects;

const Header = ({ user, isAdminLocation }: { user: AccountResponse; isAdminLocation: boolean }) => {
  return (
    <h1 className="u-t-primary u-m0">
      {isAdminLocation ? (
        <span>
          Upiti korisnika <span className="u-t-uppercase">{`${user?.firstName} ${user?.lastName}`}</span>
        </span>
      ) : (
        <span className="u-t-uppercase">Moji Projekti</span>
      )}
    </h1>
  );
};

const Row = ({
  user,
  project,
  isAdminLocation,
  handleDelete,
  onProjectRename,
  openPartnerModal,
  sendToMail,
  downloadProject,
  downloadSystemProjectPdf,
  downloadSystemProjectExcel,
}: {
  user: AccountResponse | undefined;
  project: ProjectResponse;
  isAdminLocation: boolean;
  handleDelete: (project: ProjectResponse) => void;
  onProjectRename: (projectId: number, projectName: string) => void;
  openPartnerModal: (project: ProjectResponse | undefined) => void;
  sendToMail: (projectId: number | undefined) => void;
  downloadProject: (projectId: number | undefined, projectName: string) => void;
  downloadSystemProjectPdf: (projectId: number | undefined, projectName: string) => void;
  downloadSystemProjectExcel: (projectId: number | undefined, projectName: string) => void;
}) => {
  const [renameActive, setRenameActive] = useState(false);
  const [projectName, setProjectName] = useState(project.name);
  const [showOptions, setShowOptions] = useState(false);
  const container = useRef<any>(null);

  let history = useHistory();

  const handleClickOutside = (event: MouseEvent) => {
    if (container.current && !container.current.contains(event.target)) {
      setShowOptions(false);
    }
  };

  function resetProjectName() {
    setRenameActive(false);
    setProjectName(project.name);
  }
  function handleProjectRename() {
    if (project.name !== projectName) {
      onProjectRename(Number(project.id), projectName ?? "");
    }
    setRenameActive(false);
  }
  const onInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const inputValue = event.target.value;
    setProjectName(inputValue);
  };

  function handleSendToMail() {
    setShowOptions(false);
    sendToMail(project?.id);
  }

  function handleDownloadProject() {
    setShowOptions(false);
    downloadProject(project?.id, project?.name!);
  }

  function handleDownloadSystemProjectPdf() {
    setShowOptions(false);
    downloadSystemProjectPdf(project?.id, project?.name!);
  }

  function handleDownloadSystemProjectExcel() {
    setShowOptions(false);
    downloadSystemProjectExcel(project?.id, project?.name!);
  }

  function handleOpenPartnerModal() {
    setShowOptions(false);
    openPartnerModal(project);
  }

  function duplicateProject() {
    const duplicateLink = `${ROUTES.home}?upitBroj=${project.id}`;
    history.push(duplicateLink);
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  return (
    <>
      <tr className="u-col-12" key={project.id}>
        <td className="u-w10 u-t-align-start u-t-f16p">{project.id}</td>
        <td className="u-w15 u-t-align-start u-t-f16p">
          {!renameActive && project.name}
          {renameActive && (
            <div className="u-flex u-flex-items-center">
              <input
                autoFocus
                onChange={onInputChange}
                value={projectName ?? ""}
                className={classNames("u-border-none", "u-outline-none", "u-p0", projectCSS.plainInput)}
              />
              <button onClick={() => handleProjectRename()} className="u-p0 u-mr10px u-cursor-pointer">
                <Icon name="accept" className="u-t-f30p u-t-primary-dark"></Icon>
              </button>
              <button onClick={() => resetProjectName()} className="u-p0 u-cursor-pointer">
                <Icon name="decline" className="u-t-f30p  u-t-secondary"></Icon>
              </button>
            </div>
          )}
        </td>
        <td className="u-w20 u-t-align-start u-t-f16p">{project.partnerName}</td>
        <td className="u-w10 u-t-align-start u-t-f16p">{moment(project.created).format("DD.MM.yyyy")}</td>
        {isAdminLocation ? (
          <td className="u-w45 u-t-align-end u-t-f16p">
            <Link
              to={ROUTES.showAdminUserProject(project.userId, project.id)}
              className={classNames("u-t-valign-mid", "u-t-nodecoration", "u-t-w400", "u-cursor-pointer", projectCSS.blueFillBtn)}
            >
              Otvori
            </Link>
          </td>
        ) : (
          <td className="u-t-align-end u-t-f16p u-flex u-w100 u-flex-content-end u-flex-items-center">
            <Link
              to={ROUTES.showUserProject(project.userId, project.id)}
              className={classNames(
                "u-t-nodecoration",
                "u-ml0",
                "u-t-w400",
                "u-cursor-pointer",
                renameActive ? projectCSS.linkDisabled : "",
                projectCSS.blueFillBtn
              )}
            >
              Otvori
            </Link>
            <div className="u-pos-relative u-inline-block" ref={container}>
              <button
                onClick={() => setShowOptions(!showOptions)}
                className={classNames("u-t-valign-mid", "u-hauto", "u-cursor-pointer", projectCSS.optionsBtnWrap)}
              >
                <Icon name="options" className={classNames("u-t-primary", "u-t-f18p", projectCSS.optionsButton)}></Icon>
              </button>
              {showOptions && (
                <div className={classNames(projectCSS.optionsMenuWrap)}>
                  <ul className={classNames("u-list-style-none", "u-p0", "u-m0", projectCSS.optionsMenu)}>
                    <li>
                      <button
                        disabled={renameActive}
                        onClick={() => (!renameActive ? setRenameActive(true) : {})}
                        className={classNames(
                          "u-t-nodecoration",
                          "u-t-w400",
                          renameActive ? projectCSS.btnDisabled : "u-cursor-pointer",
                          projectCSS.optionMenuItem
                        )}
                      >
                        Preimenuj
                      </button>
                    </li>
                    <li>
                      <button
                        disabled={renameActive}
                        onClick={() => (!renameActive ? duplicateProject() : {})}
                        className={classNames(
                          "u-t-nodecoration",
                          "u-t-w400",
                          renameActive ? projectCSS.btnDisabled : "u-cursor-pointer",
                          projectCSS.optionMenuItem
                        )}
                      >
                        Dupliciraj
                      </button>
                    </li>
                    {(user?.role === ROLE.Admin || user?.role === ROLE.RegionalAdmin) && (
                      <div>
                        {!project.partnerName && (
                          <li>
                            <button
                              disabled={renameActive}
                              onClick={() => (!renameActive ? handleOpenPartnerModal() : {})}
                              className={classNames(
                                "u-t-nodecoration",
                                "u-t-w400",
                                renameActive ? projectCSS.btnDisabled : "u-cursor-pointer",
                                projectCSS.optionMenuItem
                              )}
                            >
                              Pošalji partneru
                            </button>
                          </li>
                        )}
                        <li>
                          <button
                            disabled={renameActive}
                            onClick={() => (!renameActive ? handleSendToMail() : {})}
                            className={classNames(
                              "u-t-nodecoration",
                              "u-t-w400",
                              renameActive ? projectCSS.btnDisabled : "u-cursor-pointer",
                              projectCSS.optionMenuItem
                            )}
                          >
                            Pošalji na mail
                          </button>
                        </li>
                        <li>
                          <button
                            disabled={renameActive}
                            onClick={() => (!renameActive ? handleDownloadProject() : {})}
                            className={classNames(
                              "u-t-nodecoration",
                              "u-t-w400",
                              renameActive ? projectCSS.btnDisabled : "u-cursor-pointer",
                              projectCSS.optionMenuItem
                            )}
                          >
                            Preuzmi
                          </button>
                        </li>
                        <li>
                          <button
                            disabled={renameActive}
                            onClick={() => (!renameActive ? handleDownloadSystemProjectPdf() : {})}
                            className={classNames(
                              "u-t-nodecoration",
                              "u-t-w400",
                              renameActive ? projectCSS.btnDisabled : "u-cursor-pointer",
                              projectCSS.optionMenuItem
                            )}
                          >
                            Preuzmi po sustavu PDF
                          </button>
                        </li>
                        <li>
                          <button
                            disabled={renameActive}
                            onClick={() => (!renameActive ? handleDownloadSystemProjectExcel() : {})}
                            className={classNames(
                              "u-t-nodecoration",
                              "u-t-w400",
                              renameActive ? projectCSS.btnDisabled : "u-cursor-pointer",
                              projectCSS.optionMenuItem
                            )}
                          >
                            Preuzmi po sustavu xlsx
                          </button>
                        </li>
                      </div>
                    )}
                    <li>
                      <button
                        onClick={() => (!renameActive ? handleDelete(project) : {})}
                        className={classNames("u-t-valign-mid", renameActive ? projectCSS.btnDisabled : "u-cursor-pointer", projectCSS.optionMenuItemRed)}
                      >
                        Obriši
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </td>
        )}
      </tr>
    </>
  );
};

const ListItem = ({
  project,
  isAdminLocation,
  isRenameActive,
  onProjectRename,
  onProjectRenameCancel,
  openActions,
}: {
  project: ProjectResponse;
  isAdminLocation: boolean;
  isRenameActive: boolean;
  onProjectRename: (projectId: number, projectName: string) => void;
  onProjectRenameCancel: () => void;
  openActions: () => void;
}) => {
  const [renameActive, setRenameActive] = useState(isRenameActive);
  const [projectName, setProjectName] = useState(project.name);
  let history = useHistory();

  function openProject() {
    history.push(isAdminLocation ? ROUTES.showAdminUserProject(project.userId, project.id) : ROUTES.showUserProject(project.userId, project.id));
  }

  function resetProjectName() {
    setRenameActive(false);
    setProjectName(project.name);
  }
  function handleProjectRename() {
    if (project.name !== projectName) {
      onProjectRename(Number(project.id), projectName ?? "");
    }
    setRenameActive(false);
  }
  const onInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const inputValue = event.target.value;
    setProjectName(inputValue);
  };
  useEffect(() => {
    setRenameActive(isRenameActive);
  }, [isRenameActive]);
  return (
    <li key={project.id}>
      <button
        onClick={() => (!renameActive ? openProject() : null)}
        className={classNames("u-block", "u-w100", "u-p0", "u-t-nodecoration", "u-t-w400", "u-t-black", "u-cursor-pointer")}
      >
        <div className={classNames("u-bg-white", "u-flex", "u-flex-column", "u-mb5per", projectCSS.userProjects)}>
          <div className="u-flex u-col-12">
            <div className="u-flex u-flex-content-start u-col-5">
              <span className="u-t-secondary u-mr5px">šifra:</span>
              <span className="u-col-xs-5">{project.id}</span>
            </div>
            <div className="u-col-6 u-flex u-flex-content-end">
              <span className="u-t-secondary u-mr5px">kreirano:</span>
              <span className="u-t-align-end">{moment(project.created).format("DD.MM.yyyy")}</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openActions();
              }}
              className="u-col-1 u-flex u-flex-content-end u-cursor-pointer"
            >
              <img alt="option button" src={iconOptions}></img>
            </button>
          </div>
          <div className="u-col-12 u-flex -u-flex-content-between u-mt5px u-mb5px">
            <h5 className="u-m0 u-t-w400 u-t-f18p u-t-lh15 u-m0"> {!renameActive && project.name}</h5>
            {renameActive && (
              <div className="u-col-11 u-flex u-flex-items-center">
                <input
                  autoFocus
                  onChange={onInputChange}
                  value={projectName ?? ""}
                  className={classNames("u-border-none", "u-t-f18p", "u-t-lh15", "u-outline-none", "u-p0", projectCSS.plainInput)}
                />
                <button onClick={() => handleProjectRename()} className="u-flex u-mlauto u-p0 u-mr20px u-cursor-pointer">
                  <Icon name="accept" className="u-t-f27p u-t-primary-dark"></Icon>
                </button>
                <button
                  onClick={() => {
                    onProjectRenameCancel();
                    resetProjectName();
                  }}
                  className="u-flex u-p0 u-cursor-pointer"
                >
                  <Icon name="decline" className="u-t-f27p  u-t-secondary"></Icon>
                </button>
              </div>
            )}
          </div>
          <div className="u-flex u-flex-content-start u-col-11">
            <span className="u-t-secondary u-col-6 u-t-align-start">Poslano partneru:</span>
            <span className="u-col-6 u-mlauto u-t-align-end">{project.partnerName}</span>
          </div>
        </div>
      </button>
    </li>
  );
};
